import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import "./Works.css";
import { api } from "../../api/client";
import type { Work } from "../../api/types";
import { PageHeader } from "../../components/PageHeader";
import { WorkCard } from "./components/WorkCard";

const ALL_PILL = "All Works";

const CATEGORY_PILLS = [
    "Records",
    "Radio & Podcasts",
    "Soundtracks",
    "Installations",
    "Performances",
];

export default function Works() {
    const [selectedCategory, setSelectedCategory] = useState<string>(ALL_PILL);
    const [works, setWorks] = useState<Work[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        api.works
            .list()
            .then((rows) => {
                if (!cancelled) setWorks(rows);
            })
            .catch((err) => {
                if (!cancelled) setError(err.message ?? "Failed to load works");
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const filteredWorks = useMemo(() => {
        if (!works) return [];
        const sorted = [...works].sort((a, b) => {
            // Pinned works come first; within each group, newest date first.
            const aPinned = typeof a.pinnedOrder === 'number';
            const bPinned = typeof b.pinnedOrder === 'number';
            if (aPinned && !bPinned) return -1;
            if (bPinned && !aPinned) return 1;
            return new Date(b.date).getTime() - new Date(a.date).getTime();
        });
        if (selectedCategory === ALL_PILL) return sorted;
        return sorted.filter((w) => w.tags.includes(selectedCategory));
    }, [works, selectedCategory]);

    return (
        <div className="works-page">
            <Helmet>
                <title>Lo.Sai (Lorenzo Saini) - Sound Artist & Composer | Maitake EP</title>
                <meta name="description" content="Explore the portfolio of Lo.Sai (Lorenzo Saini): Maitake EP, soundtracks, installations, performances, records, and radio works blending experimental music, sound art, and creative technology." />
                <link rel="canonical" href="https://lorenzosaini.com/" />
                <meta property="og:title" content="Lo.Sai (Lorenzo Saini) - Sound Artist & Composer" />
                <meta property="og:description" content="Explore the portfolio of Lo.Sai: Maitake EP, soundtracks, installations, performances, records, and radio works blending experimental music, sound art, and creative technology." />
                <meta property="og:url" content="https://lorenzosaini.com/" />
                <meta name="twitter:title" content="Lo.Sai (Lorenzo Saini) - Sound Artist & Composer" />
                <meta name="twitter:description" content="Explore the portfolio of Lo.Sai: Maitake EP, soundtracks, installations, performances, records, and radio works blending experimental music, sound art, and creative technology." />
            </Helmet>
            <PageHeader title="WORKS" />

            <div className="category-filters">
                {[ALL_PILL, ...CATEGORY_PILLS].map((pill) => (
                    <button
                        key={pill}
                        className={`filter-pill ${selectedCategory === pill ? "active" : ""}`}
                        onClick={() => setSelectedCategory(pill)}
                    >
                        {pill}
                    </button>
                ))}
            </div>

            <div className="works-grid">
                {error && <p className="no-results">{error}</p>}
                {!error && works === null && <p className="no-results">Loading…</p>}
                {!error && works !== null && filteredWorks.length === 0 && (
                    <p className="no-results">
                        No works found under "{selectedCategory}."
                    </p>
                )}

                {filteredWorks.map((work, idx) => (
                    <WorkCard key={work.slug} work={work} index={idx} />
                ))}
            </div>
        </div>
    );
}
